import React, { useEffect, useRef, useState } from 'react';
import to from 'await-to-js';
import csvDownload from 'json-to-csv-export';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import * as Sentry from "@sentry/react";

import LoadingSpinner from 'app/partials/content/LoadingSpinner';
import { accessPermission } from 'app/common/helperFunction';

import LoyaltyTable from '../../partials/content/Loyalty/LoyaltyTable';
import ExportAndCustomFilters from '../../partials/content/ExportAndCustomFilters';
import { getLoyalties } from '../../services/loyalty';
import { hasMultipleAccounts } from '../../services/helpers/account-id';
import ResponseModal from '../Helpers/ResponseModal';
import OperatorSpecificModal from '../Helpers/OperatorSpecificModal';

import styles from '../../../_metronic/_assets/sass/pages/loyalty.module.scss';
import { useCustomResponse } from 'app/hooks/useCustomResponse';
import { reportFormats } from 'app/services/reports';
import { CANCEL_TOKEN } from 'app/common/constant';

export default function Loyalty(props) {
  const {push} = useHistory();
  const { filter: {} } = props;
  const [loyalty, setLoyalty] = useState([]);
  const [totalLocations, setTotalLocations] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10
  });
  const [isLoaded, setIsLoaded] = useState(false);
  const [operatorSpecificModalOpen, setOperatorSpecificModalOpen] = useState(false);
  const [responseValue, setResponseValue] = useCustomResponse()
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if(!accessPermission("LoyaltyAll")){
      push({pathname: "/dashboard"});
    }
  }, []);

  useEffect(() => {
    getLoyaltiesList();

  }, [pagination]);



  /**
   * Get all loyalty
   * @returns {Promise<void>}
   */
  async function getLoyaltiesList() {
    if (!isMountedRef.current) return;

    if(hasMultipleAccounts()){
      setOperatorSpecificModalOpen(true);
      return
    }

    const [err, res] = await to(getLoyalties(pagination));

    if (!isMountedRef.current) return;
    setIsLoaded(true);

    if (err){
      if (err.message === CANCEL_TOKEN) return;
      Sentry.captureException(err);
      setResponseValue({...responseValue,isMessageOpen: true, isSuccess:false,message:`Error while getting loyalty info ${err}`})
      return console.error('[Loyalty]. Error while fetching data.');

    }

    setLoyalty(res?.data?.locations?.rows ?? [])
    setTotalLocations(res?.data?.meta?.totalItems ?? 0);
 
  }

  
  async function handleExport(type) {

    const [err, res] = await to(
      getLoyalties({
        page: 1,
        limit: totalLocations,
      })
    );

    if(err) {
    if (err.message === CANCEL_TOKEN) return;
    return console.error("[Sales]. Error while fetching data. ", err);
    }
    
    const rows = res?.data?.locations?.rows ?? [];

    if(type === 'pdf'){

      const exportRows = rows.map(ele => {
        const redeemed = ele?.redeems?.[0] ?? {};
        return [
        ele.name ?? "",
        ele.pointsIssued ?? 0,
        redeemed.supplyPrice ? `$ ${redeemed.supplyPrice}` : `$ 0` ,
        redeemed.retailPrice ? `$ ${redeemed.retailPrice}` : `$ 0` ,
        redeemed.totalProducts ?? 0,
        ele.loyatlyEnabled? 'On':'Off',
       ];
      });

        const unit = "pt";
        const size = "A4";
        const orientation = "portrait";
        const marginLeft = 30;
        const doc = new jsPDF(orientation,unit, size);
        doc.setFontSize(15);

        const title = "Loyalty";
        const headers = [["Location Name", "Points Issued", "Supply Cost Redeemed", "Retail Value Redeemed", "Products Issued", "Status"]];

        let content = {
          startY: 50,
          head: headers,
          body: exportRows,
          styles: {overflow: 'linebreak', fontSize: 8},
        };

        doc.text(title, marginLeft, 30);
        autoTable(doc, content);
        doc.save(`Loyality - ${format(new Date(), 'MM/dd/yyyy')}.${type}`);

    } else if (type === 'csv') {
 
        const exportRows = [];
        rows.forEach(ele => {
          const redeemed = ele?.redeems?.[0] ?? {};
          let obj = {
           'Location Name' : ele.name ? ele.name : "",
           'Points Issued' : ele.pointsIssued ? ele.pointsIssued : "0",
           'Supply Cost Redeemed' : redeemed.supplyPrice ? `$ ${redeemed.supplyPrice}` : `$ 0` ,
           'Retail Value Redeemed': redeemed.retailPrice ? `$ ${redeemed.retailPrice}` : `$ 0`,
           'Product Issued' : redeemed.totalProducts ? redeemed.totalProducts : '0',
           'Status' : ele.loyatlyEnabled ? 'On':'Off',
          }
          exportRows.push(obj);
        })
        
      csvDownload(exportRows, `Loyalty - ${format(new Date(), 'MM/dd/yyyy')}.${type}`);
    }
  }

  return (
    <>
      <ExportAndCustomFilters onExport ={handleExport} newFilterTag={reportFormats.PDF}/>

      {!isLoaded ? (
        <LoadingSpinner/>
      ) : (
      <>
        <div className={styles.wrapper}>
          <LoyaltyTable
            rows={loyalty}
            totalLocations={totalLocations}
            pagination={pagination}
            onPaginationChange={value => setPagination(value)}
            />
        </div>
        <ResponseModal
        isSuccess={responseValue?.isSuccess}
        message={responseValue?.message}
        open={responseValue?.isMessageOpen}
        onClose={value => setResponseValue({...responseValue, isMessageOpen:value})}
        />
      </>)}
      {operatorSpecificModalOpen && (
        <OperatorSpecificModal
          open={operatorSpecificModalOpen}
          onClose={(value) => setOperatorSpecificModalOpen(value)}
        />
      )}
    </>
  );
}
